import { formatDistanceToNow } from 'date-fns';

import type {
  AISystemExecution,
  AISystemExecutionListItem,
  ActionsSummary,
  ExpertExecutionDetail,
  ExecutionStatus,
} from '../../types';

type StatusTone = 'default' | 'success' | 'error' | 'warning' | 'info';

type RunLike = Pick<
  AISystemExecutionListItem,
  'status' | 'status_display' | 'created_at' | 'completed_at' | 'execution_time_ms' | 'actions_summary'
>;

const normalizeStatus = (status: string): string => status.toUpperCase();

export const getStatusTone = (status: string): StatusTone => {
  switch (normalizeStatus(status)) {
    case 'COMPLETED':
    case 'SUCCESS':
    case 'CACHED':
      return 'success';
    case 'ERROR':
    case 'FAILED':
    case 'STOPPED':
    case 'CANCELLED':
      return 'error';
    case 'RUNNING':
      return 'info';
    case 'PENDING':
    case 'START':
      return 'warning';
    default:
      return 'default';
  }
};

export const getStatusLabel = (status: ExecutionStatus, statusDisplay?: string): string => {
  if (statusDisplay) {
    return statusDisplay;
  }

  switch (normalizeStatus(status)) {
    case 'COMPLETED':
    case 'SUCCESS':
      return 'Completed';
    case 'ERROR':
    case 'FAILED':
      return 'Failed';
    case 'RUNNING':
      return 'Running';
    case 'PENDING':
    case 'START':
      return 'Pending';
    case 'CACHED':
      return 'Cached';
    default:
      return String(status);
  }
};

export const formatDurationMs = (durationMs: number | null | undefined): string => {
  if (durationMs == null) {
    return '-';
  }

  if (durationMs >= 60000) {
    return `${(durationMs / 60000).toFixed(1)}m`;
  }

  if (durationMs >= 1000) {
    return `${(durationMs / 1000).toFixed(2)}s`;
  }

  return `${durationMs.toFixed(0)}ms`;
};

export const formatRunDuration = (run: Pick<RunLike, 'execution_time_ms' | 'created_at' | 'completed_at'>): string => {
  if (run.execution_time_ms != null) {
    return formatDurationMs(run.execution_time_ms);
  }

  if (!run.completed_at) {
    return 'In progress';
  }

  const durationMs = new Date(run.completed_at).getTime() - new Date(run.created_at).getTime();
  return formatDurationMs(durationMs);
};

export const formatMoney = (
  value: number | null | undefined,
  options: { compact?: boolean; emptyLabel?: string } = {},
): string => {
  const { compact = false, emptyLabel = '-' } = options;

  if (value == null) {
    return emptyLabel;
  }

  if (!compact) {
    return `$${value.toFixed(4)}`;
  }

  if (value === 0) {
    return '$0.00';
  }

  if (value < 0.01) {
    return '<$0.01';
  }

  if (value < 1) {
    return `$${value.toFixed(2)}`;
  }

  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const formatMemory = (bytes: number | null | undefined): string => {
  if (bytes == null || bytes <= 0) {
    return '-';
  }

  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  }

  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${bytes} B`;
};

export const formatTimestamp = (value: string | null | undefined): string => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleString();
};

export const getLoopIterationLabel = (
  result: Pick<ExpertExecutionDetail, 'iteration'>,
): string | null => {
  return result.iteration == null ? null : `Iteration ${result.iteration}`;
};

export const getLoopMetadata = (
  result: Pick<ExpertExecutionDetail, 'metadata'>,
): Record<string, any> | null => {
  const metadata = result.metadata?.for_loop;
  return metadata && typeof metadata === 'object' ? metadata : null;
};

export const formatLoopItem = (item: unknown, maxLength = 96): string => {
  if (item == null) {
    return '-';
  }

  let value: string;
  if (typeof item === 'string') {
    value = item;
  } else if (typeof item === 'number' || typeof item === 'boolean') {
    value = String(item);
  } else {
    try {
      value = JSON.stringify(item);
    } catch (_error) {
      value = String(item);
    }
  }

  return value.length > maxLength ? `${value.slice(0, maxLength - 1)}...` : value;
};

export const formatRelativeTimestamp = (value: string): string => {
  return formatDistanceToNow(new Date(value), { addSuffix: true });
};

export const getActionsSummary = (run: { actions_summary: ActionsSummary | null }): ActionsSummary | null => {
  if (!run.actions_summary || run.actions_summary.total === 0) {
    return null;
  }

  return run.actions_summary;
};

export const getRunOutcomeSummary = (run: RunLike): { tone: StatusTone; label: string; detail: string } => {
  const actionsSummary = getActionsSummary(run);
  if (actionsSummary) {
    if (actionsSummary.failed > 0) {
      return {
        tone: actionsSummary.successful > 0 ? 'warning' : 'error',
        label: `${actionsSummary.successful}/${actionsSummary.total} actions succeeded`,
        detail:
          actionsSummary.failed === 1
            ? '1 action failed during the run'
            : `${actionsSummary.failed} actions failed during the run`,
      };
    }

    return {
      tone: 'success',
      label: `${actionsSummary.total} action${actionsSummary.total === 1 ? '' : 's'} completed`,
      detail: 'Run produced downstream actions',
    };
  }

  switch (normalizeStatus(run.status)) {
    case 'RUNNING':
    case 'PENDING':
    case 'START':
      return {
        tone: 'info',
        label: 'In progress',
        detail: 'Run is still processing',
      };
    case 'ERROR':
    case 'FAILED':
    case 'STOPPED':
    case 'CANCELLED':
      return {
        tone: 'error',
        label: 'Needs attention',
        detail: 'Run ended before completing cleanly',
      };
    default:
      return {
        tone: 'default',
        label: 'No actions taken',
        detail: 'Run completed without downstream changes',
      };
  }
};

export const getExpertCost = (expert: Pick<ExpertExecutionDetail, 'total_cost' | 'compute_cost'>): number => {
  return Math.max((expert.total_cost || 0) - (expert.compute_cost || 0), 0);
};

export const getExecutionTotals = (execution: AISystemExecution): {
  totalCost: number;
  expertsCost: number;
  computeCost: number;
  totalTokens: number;
} => {
  const totalCost =
    execution.total_cost ?? execution.expert_results.reduce((sum, result) => sum + (result.total_cost || 0), 0);
  const totalTokens =
    execution.total_tokens ?? execution.expert_results.reduce((sum, result) => sum + (result.total_token || 0), 0);
  const computeCost = execution.compute_cost ?? 0;
  const expertsCost = execution.experts_cost ?? Math.max(totalCost - computeCost, 0);

  return {
    totalCost,
    expertsCost,
    computeCost,
    totalTokens,
  };
};

export const getCompletedExpertsCount = (execution: AISystemExecution): number => {
  return execution.expert_results.filter((result) => {
    const status = normalizeStatus(result.status);
    return status === 'SUCCESS' || status === 'COMPLETED' || status === 'CACHED';
  }).length;
};
